import { useTranslation } from 'react-i18next';
import { BoundedContent, Box, FlexBox, FluidImage, H2, H3, tokens, Typography } from '@vp/swan';
import { sanitiseLocale } from '../../../../i18n/config';
import { useTrackNavigationClicked } from '../../../../utils/analytics';
import { logomakerCtaPathByLocale } from '../../../../utils/urls';
import LogomakerCTA from '../LogomakerCTA/LogomakerCTA';

const cloudinaryUrl =
    'https://cms.cloudinary.vpsvc.com/image/upload/c_scale,dpr_auto,f_auto,w_auto';
const folderPath = '/vista-logos-pages/en-US/logomaker-gen-ai/how-it-works';

const HowItWorks = () => {
    const { i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);

    const howItWorksSteps = [
        {
            title: '1. Create',
            subtitle:
                'Tell us a little bit about your business and instantly see personalized logo options generated by AI.',
            image: `${cloudinaryUrl}${folderPath}/hiw-step_1.webp`,
            alt: 'AI prompt showing three logo variations of floral house designs generated from the text "Flower Nook is your local florist expert".',
        },
        {
            title: '2. Customize',
            subtitle:
                'Easily fine-tune graphics, text and colors with intuitive tools – stay in control until your logo feels just right.',
            image: `${cloudinaryUrl}${folderPath}/hiw-step_2.webp`,
            alt: 'AI prompt field with text to change the font of the selected logo to a handwritten style.',
        },
        {
            title: '3. Bring it to life',
            subtitle:
                'Download your free logo files in SVG, PDF and transparent PNG (crisp and clear at any size) to use in print or online.',
            image: `${cloudinaryUrl}${folderPath}/hiw-step_3.webp`,
            alt: 'Flower Nook logo applied on stickers, business cards and a tote bag showcasing the logo in real-world applications.',
        },
    ];

    const howToStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How Logomaker works',
        step: howItWorksSteps.map((step) => ({
            '@type': 'HowToStep',
            name: step.title,
            text: step.subtitle,
            image: step.image,
        })),
    };

    const logomakerCTARef = useTrackNavigationClicked(
        'Design Services',
        'How It Works Section CTA',
        logomakerCtaPathByLocale(locale),
        'logoMaker',
    );

    return (
        <BoundedContent overflow="hidden" bgc="info">
            <Box marginY="between-sections">
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(howToStructuredData),
                    }}
                />
                <H2 fontSkin="title-section">How does the AI logo maker work?</H2>
                <Typography marginBottom="between-subsections">
                    Create your logo in three simple steps.
                </Typography>

                <FlexBox
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    marginBottom="to-actions"
                    gap={6}
                >
                    {howItWorksSteps.map((step, index) => (
                        <HowItWorksStep
                            title={step.title}
                            subtitle={step.subtitle}
                            src={step.image}
                            alt={step.alt}
                            key={index}
                        />
                    ))}
                </FlexBox>
                <FlexBox justifyContent="center">
                    <LogomakerCTA ctaRef={logomakerCTARef} />
                </FlexBox>
            </Box>
        </BoundedContent>
    );
};

type HowItWorksStepProps = {
    title: string;
    subtitle: string;
    src: string;
    alt: string;
};

const HowItWorksStep = ({ title, subtitle, src, alt }: HowItWorksStepProps) => {
    return (
        <FlexBox style={{ flex: 1 }} flexDirection="column">
            <FluidImage
                src={src}
                alt={alt}
                style={{ borderRadius: tokens.SwanSemBorderRadiusStrong }}
            />
            <H3 fontSkin="title-subsection" marginY="between-actions">
                {title}
            </H3>
            <Typography>{subtitle}</Typography>
        </FlexBox>
    );
};

export default HowItWorks;
